/*
 * Ops statement + sheet compare: every rule lives here, pure (no DOM, no network), so the page is a
 * thin renderer. The uploaded sheet is parsed in the browser only; nothing is sent or saved.
 */
import { cell_text, to_csv_text } from './csv';

export const MAX_SHEET_BYTES = 2 * 1024 * 1024;
export const TOLERANCE = 1; // rupees: differences up to ₹1 are rounding, not a mismatch
const EPS = 1e-6;

const ID_KEY = 'teacherid';
// Month-labelled headers ("Grand Total\nSep-26", "Last month Payment ( Aug 26 )") match by prefix.
const PREFIX_KEYS = ['grandtotal', 'lastmonthpayment'];
const NAME_KEYS = ['teacherfullname', 'teachername'];

// Case, space and punctuation insensitive ("&" reads as "and"): "Late Audio/Video Turn On" -> "lateaudiovideoturnon".
export const normalize_header = (h) =>
  String(h === null || h === undefined ? '' : h)
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');

export const header_match_key = (h) => {
  const n = normalize_header(h);
  return PREFIX_KEYS.find((p) => n.startsWith(p)) || n;
};

const round2 = (n) => Math.round(n * 100) / 100;

/*
 * Sheet cell -> number. Tolerates ₹/Rs/INR, commas, spaces, U+2212, a trailing or leading sign and
 * accounting negatives "(1,200)". Blank (or a lone dash) is 0 for comparison. NaN when unreadable.
 */
export const parse_sheet_number = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  let s = String(raw === null || raw === undefined ? '' : raw)
    .replace(/[\u00A0\s]/g, '')
    .replace(/\u2212/g, '-')
    .replace(/₹|rs\.?|inr/gi, '')
    .replace(/,/g, '');
  if (s === '' || s === '-') return 0;
  let neg = false;
  const paren = /^\((.*)\)$/.exec(s);
  if (paren) {
    neg = true;
    s = paren[1];
  }
  if (/^-?\d*\.?\d+-$/.test(s)) s = `-${s.slice(0, -1)}`; // "120-" (some exports)
  if (!/^[-+]?(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
  const n = Number(s);
  return neg ? -Math.abs(n) : n;
};

// Our wire value -> number; null/blank is 0 like a blank sheet cell.
const ours_number = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// "360", "360.0", " 360 " -> "360"; blank -> ''.
export const normalize_teacher_id = (v) => {
  const s = String(v === null || v === undefined ? '' : v).trim();
  return /^\d+\.0+$/.test(s) ? s.replace(/\.0+$/, '') : s;
};

/* RFC 4180 parser: quoted fields with commas, quotes ("") and newlines; CRLF or LF; BOM stripped. */
export const parse_csv = (text) => {
  const src = String(text || '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else quoted = false;
      } else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  // Spreadsheet exports pad with empty lines; they carry no data.
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
};

// File picked -> error text, or '' when it may be read.
export const check_sheet_file = (file) => {
  if (!file) return 'Choose a CSV file.';
  if (!/\.csv$/i.test(file.name || '')) return 'Upload a .csv file (File > Download > CSV).';
  if (file.size > MAX_SHEET_BYTES) return 'The file is over 2 MB. Upload only the statement sheet.';
  if (!file.size) return 'The file is empty.';
  return '';
};

/* ---------- statement (API) ---------- */

const month_header = (statement, col) => {
  const key = header_match_key(col.header);
  if (key === 'grandtotal' && statement.grand_total_header) return statement.grand_total_header;
  if (key === 'lastmonthpayment' && statement.last_month_header) return statement.last_month_header;
  return col.header;
};

// The statement as the page uses it: headers with the month labels, rows by teacher name.
export const statement_view = (data) => {
  const d = data || {};
  const columns = (Array.isArray(d.columns) ? d.columns : []).map((c) => ({
    key: c.key,
    kind: c.kind === 'money' || c.kind === 'number' ? c.kind : 'text',
    source: c.source || '',
    header: month_header(d, c),
    // Added after the Sep-26 sheet template: an older upload may not have it (not a gap).
    optional: c.optional === true
  }));
  const unmapped = Array.isArray(d.unmapped) ? d.unmapped : [];
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    teacher_id: normalize_teacher_id(r.teacher_id),
    teacher_uuid: r.teacher_uuid || '',
    // false: the month's lines do not add up to its Grand Total (waiting for a recompute).
    ties_out: r.ties_out !== false,
    values: r.values || {}
  }));
  const name_cols = NAME_KEYS.map((k) =>
    columns.find((c) => header_match_key(c.header) === k)
  ).filter(Boolean);
  const name_of = (r) =>
    name_cols.map((c) => r.values[c.key]).find((v) => v !== null && v !== undefined && v !== '') ||
    '';
  const named = rows.map((r) => ({ ...r, teacher_name: String(name_of(r)) }));
  named.sort(
    (a, b) =>
      a.teacher_name.localeCompare(b.teacher_name, 'en', { sensitivity: 'base' }) ||
      a.teacher_id.localeCompare(b.teacher_id, 'en', { numeric: true })
  );
  return { month: d.month || '', columns, rows: named, unmapped };
};

// Download: exactly the sheet's headers and order, plain numbers, blank for null.
export const statement_csv = (view, opts) =>
  to_csv_text(
    view.columns.map((c) => c.header),
    view.rows.map((r) => view.columns.map((c) => cell_text(r.values[c.key], c.kind))),
    opts
  );

/* ---------- sheet ---------- */

/*
 * CSV text -> { error } or { headers, by_id, duplicate_ids, rows_without_id }. The first row is the
 * header row; teacher_id is required.
 */
export const read_sheet = (text) => {
  const rows = parse_csv(text);
  if (!rows.length) return { error: 'The sheet has no rows.' };
  const headers = rows[0];
  const id_index = headers.findIndex((h) => header_match_key(h) === ID_KEY);
  if (id_index === -1)
    return { error: 'The sheet has no teacher_id column, so rows cannot be matched.' };
  const by_id = new Map();
  const duplicate_ids = [];
  let rows_without_id = 0;
  rows.slice(1).forEach((cells) => {
    const id = normalize_teacher_id(cells[id_index]);
    if (!id) {
      rows_without_id += 1;
      return;
    }
    if (by_id.has(id)) {
      if (!duplicate_ids.includes(id)) duplicate_ids.push(id);
      return; // first row wins
    }
    by_id.set(id, cells);
  });
  if (!by_id.size) return { error: 'The sheet has a header row but no teacher rows.' };
  return { headers, id_index, by_id, duplicate_ids, rows_without_id };
};

/* ---------- compare ---------- */

/*
 * statement view + read sheet -> every compared cell and the lists around it.
 * cells: [{ teacher_id, teacher_name, key, header, sheet, sheet_text, ours, diff, differs, invalid }]
 * diff = ours - sheet, rounded to the paisa; differs when |diff| > tolerance or the sheet cell is
 * not a number.
 */
export const compare_sheet = (view, sheet, tolerance = TOLERANCE) => {
  const unmapped_keys = new Set(view.unmapped.map((u) => u.key));
  // A sheet header matches our header first, else our key ("one_on_one", "One On One").
  const ours_by_match = new Map();
  view.columns.forEach((c) => {
    const k = header_match_key(c.header);
    if (!ours_by_match.has(k)) ours_by_match.set(k, c);
  });
  view.columns.forEach((c) => {
    const k = normalize_header(c.key);
    if (k && !ours_by_match.has(k)) ours_by_match.set(k, c);
  });
  const unmapped_by_match = new Map();
  view.unmapped.forEach((u) => {
    unmapped_by_match.set(header_match_key(u.header), u);
    unmapped_by_match.set(normalize_header(u.key), u);
  });

  // Sheet column index -> our column (compared ones only); the rest is sorted into the lists.
  const compared = [];
  const unrecognised = [];
  const seen = new Set();
  sheet.headers.forEach((h, i) => {
    if (i === sheet.id_index) return;
    const k = header_match_key(h);
    if (!k) return; // unnamed padding column
    const col = ours_by_match.get(k);
    if (!col) {
      if (!unmapped_by_match.has(k)) unrecognised.push(String(h));
      return;
    }
    if (seen.has(col.key)) {
      unrecognised.push(`${h} (repeated column)`);
      return;
    }
    seen.add(col.key);
    if (col.kind === 'text' || unmapped_keys.has(col.key) || k === ID_KEY) return;
    compared.push({ index: i, col });
  });
  const absent = view.columns
    .filter((c) => c.kind !== 'text' && !unmapped_keys.has(c.key))
    .filter((c) => header_match_key(c.header) !== ID_KEY && !seen.has(c.key));
  const missing_in_sheet = absent.filter((c) => !c.optional).map((c) => c.header);
  const newer_not_in_sheet = absent.filter((c) => c.optional).map((c) => c.header);

  // Full name first, then the short name: the first one filled in.
  const name_indexes = NAME_KEYS.map((k) =>
    sheet.headers.findIndex((h) => header_match_key(h) === k)
  ).filter((i) => i !== -1);
  const sheet_name = (cells) =>
    name_indexes.map((i) => String(cells[i] || '').trim()).find(Boolean) || '';

  const ours_ids = new Set(view.rows.map((r) => r.teacher_id));
  const cells = [];
  view.rows.forEach((r) => {
    const s = sheet.by_id.get(r.teacher_id);
    if (!s) return;
    compared.forEach(({ index, col }) => {
      const sheet_text = s[index] === undefined ? '' : String(s[index]);
      const sheet_n = parse_sheet_number(sheet_text);
      const invalid = Number.isNaN(sheet_n);
      const ours = round2(ours_number(r.values[col.key]));
      const diff = invalid ? null : round2(ours - sheet_n);
      cells.push({
        teacher_id: r.teacher_id,
        teacher_name: r.teacher_name,
        ties_out: r.ties_out,
        key: col.key,
        header: col.header,
        sheet: invalid ? null : round2(sheet_n),
        sheet_text,
        ours,
        diff,
        invalid,
        differs: invalid || Math.abs(diff) > tolerance + EPS
      });
    });
  });

  const only_in_sheet = [...sheet.by_id.entries()]
    .filter(([id]) => !ours_ids.has(id))
    .map(([id, c]) => ({ teacher_id: id, teacher_name: sheet_name(c) }));
  const only_in_ours = view.rows
    .filter((r) => !sheet.by_id.has(r.teacher_id))
    .map((r) => ({ teacher_id: r.teacher_id, teacher_name: r.teacher_name, ties_out: r.ties_out }));
  // ties_out=false: a warning per teacher (lines do not add up to Grand Total yet), never a blocker.
  const not_tying_out = view.rows
    .filter((r) => !r.ties_out)
    .map((r) => ({ teacher_id: r.teacher_id, teacher_name: r.teacher_name }));
  const not_compared = view.unmapped.map((u) => ({ header: u.header, reason: u.reason || '' }));

  return {
    cells,
    summary: summarize(
      cells,
      compared.map((c) => c.col.header)
    ),
    only_in_sheet,
    only_in_ours,
    not_tying_out,
    unrecognised,
    not_compared,
    missing_in_sheet,
    newer_not_in_sheet,
    duplicate_ids: sheet.duplicate_ids,
    rows_without_id: sheet.rows_without_id
  };
};

// Totals over the differing cells: per category count of teachers, + sum, - sum, net.
export const summarize = (cells, headers = []) => {
  const teachers = new Set(cells.map((c) => c.teacher_id));
  const diff_teachers = new Set(cells.filter((c) => c.differs).map((c) => c.teacher_id));
  const by = new Map(
    headers.map((h) => [
      h,
      { header: h, teachers: 0, positive: 0, negative: 0, net: 0, invalid: 0 }
    ])
  );
  cells
    .filter((c) => c.differs)
    .forEach((c) => {
      const t = by.get(c.header) || {
        header: c.header,
        teachers: 0,
        positive: 0,
        negative: 0,
        net: 0,
        invalid: 0
      };
      t.teachers += 1;
      if (c.invalid) t.invalid += 1;
      else if (c.diff > 0) t.positive = round2(t.positive + c.diff);
      else t.negative = round2(t.negative + c.diff);
      t.net = round2(t.positive + t.negative);
      by.set(c.header, t);
    });
  return {
    teachers_compared: teachers.size,
    teachers_with_differences: diff_teachers.size,
    // A cell we could not read is counted apart, not as a difference.
    differences: cells.filter((c) => c.differs && !c.invalid).length,
    unreadable: cells.filter((c) => c.invalid).length,
    // Only categories that differ, in sheet order.
    categories: [...by.values()].filter((t) => t.teachers > 0),
    net: round2(cells.reduce((s, c) => (c.differs && !c.invalid ? s + c.diff : s), 0))
  };
};

/* ---------- results view ---------- */

export const DEFAULT_SORT = { key: 'abs_diff', dir: 'desc' };

const matches_teacher = (c, q) => {
  const t = String(q || '')
    .trim()
    .toLowerCase();
  return !t || c.teacher_id === t || c.teacher_name.toLowerCase().includes(t);
};

// Header click: the same column flips direction; a new one starts at its natural order.
export const next_sort = (sort, key) => {
  if (sort && sort.key === key) return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
  return key === 'teacher' ? { key, dir: 'asc' } : DEFAULT_SORT;
};

// filters: { category: header|'', teacher: text, only_differences: bool }
export const filter_cells = (
  cells,
  { category = '', teacher = '', only_differences = true } = {}
) =>
  cells.filter(
    (c) =>
      (!only_differences || c.differs) &&
      (!category || c.header === category) &&
      matches_teacher(c, teacher)
  );

// abs_diff (an unreadable sheet cell sorts as the largest) or teacher (name, then category order).
export const sort_cells = (cells, sort = DEFAULT_SORT) => {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const abs = (c) => (c.invalid ? Infinity : Math.abs(c.diff));
  return cells
    .map((c, i) => ({ c, i }))
    .sort((a, b) => {
      if (sort.key === 'teacher') {
        const by =
          a.c.teacher_name.localeCompare(b.c.teacher_name, 'en', { sensitivity: 'base' }) ||
          a.c.teacher_id.localeCompare(b.c.teacher_id, 'en', { numeric: true });
        return by * dir || a.i - b.i;
      }
      const d = abs(a.c) === abs(b.c) ? 0 : abs(a.c) > abs(b.c) ? 1 : -1;
      return d * dir || a.i - b.i;
    })
    .map((x) => x.c);
};

export const DIFF_HEADERS = [
  'Teacher ID',
  'Teacher',
  'Category',
  'Your sheet',
  'Our calculation',
  'Difference (ours - sheet)'
];

export const differences_csv = (cells, opts) =>
  to_csv_text(
    DIFF_HEADERS,
    cells.map((c) => [
      c.teacher_id,
      c.teacher_name,
      c.header,
      c.invalid ? c.sheet_text : cell_text(c.sheet, 'money'),
      cell_text(c.ours, 'money'),
      c.invalid ? '' : cell_text(c.diff, 'money')
    ]),
    opts
  );

const pad = (n) => String(n).padStart(2, '0');
// "teacher-payments-statement-2026-09-20261101-1530.csv" (IST stamp).
export const file_name = (kind, month, now = new Date()) => {
  const d = new Date(now.getTime() + 330 * 60000);
  const stamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}`;
  return `teacher-payments-${kind}-${month || 'month'}-${stamp}.csv`;
};
